/**
 * Central Formatting Service
 * 
 * Provides config-aware formatting for dates, times, currencies, and numbers.
 * This service is the single source of truth for all formatting operations,
 * ensuring consistency across the entire application based on platform configuration.
 * 
 * Features:
 * - Date/Time formatting with timezone support
 * - Currency formatting with locale support
 * - Number formatting with locale support
 * - Language/locale management
 * - Platform config integration
 */

import { formatDistance, formatRelative } from 'date-fns';
import type { Locale } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { enUS, es, fr, de, ja, zhCN, ko } from 'date-fns/locale';

/**
 * Platform configuration interface
 */
export interface PlatformConfig {
  companyName: string;
  platformName: string;
  timezone: string;
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  currency: string;
  defaultLanguage: string;
  paginationSize: number;
}

/**
 * Date-fns locale mapping
 */
const LOCALE_MAP: Record<string, Locale> = {
  'en': enUS,
  'en-US': enUS,
  'es': es,
  'es-ES': es,
  'fr': fr,
  'fr-FR': fr,
  'de': de,
  'de-DE': de,
  'ja': ja,
  'ja-JP': ja,
  'zh': zhCN,
  'zh-CN': zhCN,
  'ko': ko,
  'ko-KR': ko,
};

/**
 * Currency symbols mapping
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'Fr',
  INR: '₹',
  SGD: 'S$',
  HKD: 'HK$',
};

/**
 * Currency decimal places
 */
const CURRENCY_DECIMALS: Record<string, number> = {
  USD: 2, EUR: 2, GBP: 2, AUD: 2, CAD: 2, CHF: 2, INR: 2, SGD: 2, HKD: 2,
  JPY: 0, KRW: 0, // No decimals for these currencies
  CNY: 2,
};

/**
 * Central Formatting Service Class
 */
export class FormattingService {
  private config: PlatformConfig;
  private locale: Locale;
  private intlLocale: string;

  constructor(config?: Partial<PlatformConfig>) {
    // Default configuration
    this.config = {
      companyName: 'Cnapse Insight',
      platformName: 'Cnapse Cleanroom Intelligence (CCI)',
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      currency: 'USD',
      defaultLanguage: 'en-US',
      paginationSize: 25,
      ...config,
    };

    // Set locale
    this.locale = LOCALE_MAP[this.config.defaultLanguage] || enUS;
    this.intlLocale = this.config.defaultLanguage;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<PlatformConfig>): void {
    this.config = { ...this.config, ...config };
    this.locale = LOCALE_MAP[this.config.defaultLanguage] || enUS;
    this.intlLocale = this.config.defaultLanguage;
  }

  /**
   * Get current configuration
   */
  getConfig(): PlatformConfig {
    return { ...this.config };
  }

  // ==================== DATE FORMATTING ====================

  /**
   * Convert platform date format to date-fns format
   */
  private convertDateFormat(configFormat: string): string {
    const formatMap: Record<string, string> = {
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };
    return formatMap[configFormat] || 'MM/dd/yyyy';
  }

  /**
   * Get time format string
   */
  private getTimeFormat(): string {
    return this.config.timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
  }

  /**
   * Format a date (date only, no time)
   */
  formatDate(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';

      const dateFormat = this.convertDateFormat(this.config.dateFormat);
      return formatInTimeZone(dateObj, this.config.timezone, dateFormat, { locale: this.locale });
    } catch (_error) {
      console.error('Date formatting error:', _error);
      return '';
    }
  }

  /**
   * Format a time (time only, no date)
   */
  formatTime(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';

      const timeFormat = this.getTimeFormat();
      return formatInTimeZone(dateObj, this.config.timezone, timeFormat, { locale: this.locale });
    } catch (_error) {
      console.error('Time formatting error:', _error);
      return '';
    }
  }

  /**
   * Format date and time together
   */
  formatDateTime(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';

      const dateFormat = this.convertDateFormat(this.config.dateFormat);
      const timeFormat = this.getTimeFormat();
      const combinedFormat = `${dateFormat} ${timeFormat}`;
      
      return formatInTimeZone(dateObj, this.config.timezone, combinedFormat, { locale: this.locale });
    } catch (_error) {
      console.error('DateTime formatting error:', _error);
      return '';
    }
  }

  /**
   * Format relative time (e.g., "2 hours ago")
   */
  formatRelativeTime(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';

      return formatDistance(dateObj, new Date(), { 
        addSuffix: true,
        locale: this.locale 
      });
    } catch (_error) {
      console.error('Relative time formatting error:', _error);
      return '';
    }
  }

  /**
   * Format date relative to now (e.g., "yesterday", "last Friday")
   */
  formatRelativeDate(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';

      return formatRelative(dateObj, new Date(), { locale: this.locale });
    } catch (_error) {
      console.error('Relative date formatting error:', _error);
      return '';
    }
  }

  /**
   * Convert date to configured timezone
   */
  toConfiguredTimezone(date: Date | string | number): Date {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    return toZonedTime(dateObj, this.config.timezone);
  }

  /**
   * Format date for CSV export (ISO format)
   */
  formatDateForExport(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';
      
      return dateObj.toISOString().split('T')[0];
    } catch (_error) {
      console.error('Export date formatting error:', _error);
      return '';
    }
  }

  /**
   * Format datetime for CSV export (ISO format)
   */
  formatDateTimeForExport(date: Date | string | number | null | undefined): string {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';
      
      return dateObj.toISOString();
    } catch (_error) {
      console.error('Export datetime formatting error:', _error);
      return '';
    }
  }

  // ==================== CURRENCY FORMATTING ====================

  /**
   * Format currency amount
   */
  formatCurrency(
    amount: number | string | null | undefined,
    options?: {
      currency?: string;
      showSymbol?: boolean;
      decimals?: number;
    }
  ): string {
    if (amount === null || amount === undefined || amount === '') return '';
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '';

    const currency = options?.currency || this.config.currency;
    const showSymbol = options?.showSymbol !== false;
    const decimals = options?.decimals ?? CURRENCY_DECIMALS[currency] ?? 2;

    try {
      // Use Intl.NumberFormat for locale-aware formatting
      const formatter = new Intl.NumberFormat(this.intlLocale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

      return formatter.format(numAmount);
    } catch (_error) {
      // Fallback to manual formatting
      const symbol = showSymbol ? (CURRENCY_SYMBOLS[currency] || currency) : '';
      return `${symbol}${numAmount.toFixed(decimals)}`;
    }
  }

  /**
   * Format currency amount without symbol
   */
  formatCurrencyAmount(amount: number | string | null | undefined): string {
    if (amount === null || amount === undefined || amount === '') return '';
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '';

    const decimals = CURRENCY_DECIMALS[this.config.currency] ?? 2;
    
    try {
      const formatter = new Intl.NumberFormat(this.intlLocale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return formatter.format(numAmount);
    } catch (_error) {
      return numAmount.toFixed(decimals);
    }
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(currency?: string): string {
    const curr = currency || this.config.currency;
    return CURRENCY_SYMBOLS[curr] || curr;
  }

  /**
   * Parse currency string to number
   */
  parseCurrency(value: string): number {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
  }

  /**
   * Format currency range
   */
  formatCurrencyRange(min: number, max: number): string {
    const minFormatted = this.formatCurrency(min);
    const maxFormatted = this.formatCurrency(max);
    return `${minFormatted} - ${maxFormatted}`;
  }

  // ==================== NUMBER FORMATTING ====================

  /**
   * Format number with locale-specific thousands separators
   */
  formatNumber(value: number | string | null | undefined, decimals: number = 0): string {
    if (value === null || value === undefined || value === '') return '';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';

    try {
      const formatter = new Intl.NumberFormat(this.intlLocale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return formatter.format(numValue);
    } catch (_error) {
      return numValue.toFixed(decimals);
    }
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number | string | null | undefined, decimals: number = 1): string {
    if (value === null || value === undefined || value === '') return '';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';

    try {
      const formatter = new Intl.NumberFormat(this.intlLocale, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return formatter.format(numValue / 100);
    } catch (_error) {
      return `${numValue.toFixed(decimals)}%`;
    }
  }

  /**
   * Format file size (bytes to human readable)
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  // ==================== LANGUAGE/LOCALE ====================

  /**
   * Get current locale
   */
  getCurrentLocale(): string {
    return this.intlLocale;
  }

  /**
   * Get date-fns locale object
   */
  getDateFnsLocale(): Locale {
    return this.locale;
  }

  /**
   * Translate text (placeholder for future i18n integration)
   */
  translate(key: string, params?: Record<string, string | number>): string {
    // TODO: Integrate with i18n library (react-i18next, etc.)
    // For now, return the key
    let text = key;
    
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(`{${param}}`, String(params[param]));
      });
    }
    
    return text;
  }
}

/**
 * Singleton instance (default)
 */
export const defaultFormattingService = new FormattingService();

/**
 * Export service class
 */
export default FormattingService;
